import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errDetail, fmtIST } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { EmptyState, CitationList } from "@/components/common";
import { Textarea } from "@/components/ui/textarea";
import { Users, Sparkles, GraduationCap, Check, X, ChevronDown, ChevronRight, AlertTriangle } from "lucide-react";
import { CoachingDocumentView } from "@/components/CoachingDocumentView";
import { PERIOD_OPTIONS, istToday, useConsultantPerformanceUrlState } from "@/hooks/useConsultantPeriod";
import { ReportScopeBanner } from "@/components/ReportScopeBanner";
import { isFamilyRoot } from "@/lib/reportScope";

const scoreColor = (v) => {
  if (v == null) return "text-muted-foreground";
  if (v >= 80) return "text-emerald-600 dark:text-emerald-400";
  if (v >= 60) return "text-amber-600 dark:text-amber-400";
  return "text-red-600 dark:text-red-400";
};

// A provisional score is rendered in a neutral tone — it is an evidence-
// limited estimate, never a proven good/bad result.
const qualityColor = (q) => (q?.provisional ? "text-amber-700 dark:text-amber-400" : scoreColor(q?.value));

const fmtScore = (s) => (s?.value != null ? Math.round(s.value) : "—");

// Round 12, Workstream G — "every score needs an adjacent plain-language
// interpretation" (e.g. "41/100 — below where we'd want it"). Mirrors
// consultant_coaching._plain_score_band's own wording exactly (a plain,
// honest descriptive band — never a fabricated statistical "expected
// range" this codebase has no real baseline for) so the same score never
// reads two different ways across the coaching document and this panel.
const scoreInterpretation = (value) => {
  if (value == null) return null;
  if (value > 80) return "strong";
  if (value > 60) return "solid, with room to grow";
  if (value > 40) return "below where we'd want it";
  return "well below where we'd want it";
};
// Mirrors consultant_intelligence.QUALITY_METHODOLOGY_VERSION /
// EFFORT_METHODOLOGY_VERSION. A snapshot stamped with an older version (or
// none) predates the current formula and is labelled Legacy — never
// compared to a current score as if equivalent. v7 (Workstream C) =
// provisional, evidence-tier-capped Quality.
const CURRENT_QUALITY_METHODOLOGY_VERSION = 7;
const CURRENT_EFFORT_METHODOLOGY_VERSION = 3;

const ComponentBreakdown = ({ components, keys }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-xs">
    {keys.map((k) => {
      const c = components?.[k];
      return (
        <div key={k} className="flex justify-between gap-2">
          <span className="text-muted-foreground capitalize">{k.replaceAll("_", " ")}</span>
          <span className="font-mono tabular-nums">{c?.score != null ? c.score.toFixed(1) : "—"}</span>
        </div>
      );
    })}
  </div>
);

const EFFORT_KEYS = ["new_lead_sla", "followup_completion", "consultations_completed",
  "feedback_submission", "task_completion", "activity_volume", "avoidable_cancellations", "crm_hygiene"];
// Quality's numeric dimensions (consultant_intelligence.QUALITY_FACTOR_KEYS).
// Commercial results and AI-estimated Customer Momentum are separate
// surfaces below — never part of Quality.
const QUALITY_KEYS = ["feedback_documentation_substance", "customer_understanding",
  "objection_discovery", "objection_handling", "customer_alignment", "followup_planning"];
const QUALITY_LABELS = {
  feedback_documentation_substance: "Feedback documentation", customer_understanding: "Customer understanding",
  objection_discovery: "Objection discovery", objection_handling: "Objection handling",
  customer_alignment: "Customer alignment", followup_planning: "Follow-up planning",
};
const COMMERCIAL_RESULT_KEYS = ["lead_quality_adjusted_closure", "conversion_consistency"];

// "We don't know" must never look like "we know it's poor".
const MEASUREMENT = {
  evidenced: { label: "Evidenced", tone: "border-emerald-500/50 text-emerald-700 dark:text-emerald-400" },
  operational_only: { label: "CRM records only", tone: "border-blue-500/50 text-blue-700 dark:text-blue-400" },
  not_demonstrated: { label: "Measured — not shown", tone: "border-red-500/50 text-red-700 dark:text-red-400" },
  not_applicable: { label: "Not applicable", tone: "" },
  not_enough_evidence: { label: "Not enough evidence", tone: "border-dashed text-muted-foreground" },
};

// ---------------------------------------------------------------------------
// Coaching citations — resolved and authorization-checked entirely backend-
// side from the document's own immutable evidence manifest.
// ---------------------------------------------------------------------------
export const useCoachingCitations = (coachingId) => {
  const [state, setState] = useState({ all: null, byEvidenceId: {} });
  useEffect(() => {
    if (!coachingId) { setState({ all: null, byEvidenceId: {} }); return; }
    api.get(`/ai/coaching/${coachingId}/citations`)
      .then((r) => setState({ all: r.data?.all || [], byEvidenceId: r.data?.by_evidence_id || {} }))
      .catch(() => setState({ all: [], byEvidenceId: {} }));
  }, [coachingId]);
  return state;
};

const COACHING_STATUS_TONE = {
  pending_review: "bg-amber-500/15 text-amber-600 dark:text-amber-400",
  released: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400",
  rejected: "bg-red-500/15 text-red-600 dark:text-red-400",
};

const CoachingSection = ({ userId, periodQuery }) => {
  const { hasCap } = useAuth();
  const [doc, setDoc] = useState(undefined);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editedSummary, setEditedSummary] = useState("");

  const load = () => {
    api.get(`/ai/coaching/${userId}/current?${periodQuery}`)
      .then((r) => setDoc(r.data)).catch(() => setDoc(null));
  };
  useEffect(() => { setDoc(undefined); load(); setEditing(false); }, [userId, periodQuery]); // eslint-disable-line react-hooks/exhaustive-deps
  const { byEvidenceId } = useCoachingCitations(doc?.coaching_id);

  if (!hasCap("ai.coaching.review") && !hasCap("ai.coaching.generate")) return null;

  const generate = async () => {
    setBusy(true);
    try {
      await api.post(`/ai/coaching/${userId}/generate?${periodQuery}`);
      toast.success("Coaching generated — pending your review");
      load();
    } catch (e) { toast.error(errDetail(e, "AI unavailable, or no eligible activity this period")); }
    finally { setBusy(false); }
  };

  const release = async () => {
    setBusy(true);
    try {
      const body = {};
      if (editing) body.edited_content = { ...doc.coaching, executive_summary: editedSummary };
      await api.post(`/ai/coaching/${doc.coaching_id}/release`, body);
      toast.success("Released to the consultant");
      load();
    } catch (e) { toast.error(errDetail(e)); } finally { setBusy(false); }
  };

  const reject = async () => {
    const reason = window.prompt("Optional reason (kept in the audit trail, not shown to the consultant):") || null;
    setBusy(true);
    try {
      await api.post(`/ai/coaching/${doc.coaching_id}/reject`, { reason });
      toast.success("Rejected");
      load();
    } catch (e) { toast.error(errDetail(e)); } finally { setBusy(false); }
  };

  return (
    <CardContent className="pt-0 border-t mt-1 space-y-2" data-testid="coaching-section">
      <div className="flex items-center justify-between gap-2 mt-3 flex-wrap">
        <p className="text-[10px] text-muted-foreground uppercase tracking-wide flex items-center gap-1">
          <GraduationCap className="h-3.5 w-3.5" />Consultant Coaching
        </p>
        <div className="flex items-center gap-1.5">
          {doc?.status && <Badge className={COACHING_STATUS_TONE[doc.status] || ""}>{doc.status.replaceAll("_", " ")}</Badge>}
          {hasCap("ai.coaching.generate") && (
            <Button size="sm" variant="outline" className="h-7 text-xs" disabled={busy} onClick={generate}
              data-testid="coaching-generate-button">
              {busy ? "Generating…" : doc ? "Regenerate" : "Generate"}
            </Button>
          )}
        </div>
      </div>
      {doc === undefined ? null : !doc ? (
        <p className="text-xs text-muted-foreground">No coaching generated yet for this exact period.</p>
      ) : doc.content_status === "legacy_corrupted" ? (
        <p className="text-xs text-muted-foreground italic" data-testid={`coaching-doc-corrupted-${doc.coaching_id}`}>
          This older AI content cannot be displayed safely. Regenerate it to create a verified version.
        </p>
      ) : (
        <div className="space-y-2">
          {editing && (
            <div className="space-y-1 text-xs">
              <label className="text-muted-foreground">Executive summary (edit before release):</label>
              <Textarea className="text-xs" rows={3} value={editedSummary} onChange={(e) => setEditedSummary(e.target.value)} />
            </div>
          )}
          <CoachingDocumentView doc={doc} byEvidenceId={byEvidenceId} showManagementNotes={hasCap("ai.coaching.review")} />
          {doc.status === "pending_review" && hasCap("ai.coaching.review") && (
            <div className="flex items-center gap-1.5 pt-1 flex-wrap">
              <Button size="sm" className="h-7 text-xs" disabled={busy} onClick={release}
                data-testid={`coaching-release-${doc.coaching_id}`}>
                <Check className="h-3.5 w-3.5 mr-1" />{editing ? "Save & release" : "Release"}
              </Button>
              <Button size="sm" variant="outline" className="h-7 text-xs" disabled={busy}
                onClick={() => {
                  if (!editing) {
                    const c = doc.coaching;
                    setEditedSummary(c.executive_summary || [c.overall_read?.strengths_summary, c.overall_read?.biggest_opportunity].filter(Boolean).join(" "));
                  }
                  setEditing(!editing);
                }}>
                {editing ? "Cancel edit" : "Edit"}
              </Button>
              <Button size="sm" variant="ghost" className="h-7 text-xs" disabled={busy} onClick={reject}
                data-testid={`coaching-reject-${doc.coaching_id}`}>
                <X className="h-3.5 w-3.5 mr-1" />Reject
              </Button>
            </div>
          )}
          {doc.status === "rejected" && doc.rejection_reason && (
            <p className="text-[10px] text-muted-foreground">Rejected: {doc.rejection_reason}</p>
          )}
          {doc.status === "released" && (
            <p className="text-[10px] text-muted-foreground">
              Released {fmtIST(doc.decided_at)} · visible to the consultant now
              {doc.edited_before_release ? " (edited before release)" : ""}.
            </p>
          )}
        </div>
      )}
    </CardContent>
  );
};

const QualityEvidenceSection = ({ userId, periodQuery, hasEvidenceIds }) => {
  const [citationsByDim, setCitationsByDim] = useState(null);
  useEffect(() => {
    if (!hasEvidenceIds) { setCitationsByDim(null); return; }
    api.get(`/ai/consultants/${userId}/quality-citations?${periodQuery}`)
      .then((r) => setCitationsByDim(r.data)).catch(() => setCitationsByDim(null));
  }, [userId, periodQuery, hasEvidenceIds]);
  const dims = citationsByDim ? Object.entries(citationsByDim).filter(([, c]) => c?.length > 0) : [];
  if (dims.length === 0) return null;
  return (
    <details className="text-xs" data-testid="quality-evidence-citations">
      <summary className="cursor-pointer text-[10px] text-muted-foreground uppercase tracking-wide">Quality evidence records</summary>
      <div className="space-y-1.5 mt-1">
        {dims.map(([dimKey, citations]) => {
          const unique = citations.filter((c, i) => citations.findIndex((x) => x.text === c.text) === i);
          return (
            <div key={dimKey}>
              <p className="text-[10px] text-muted-foreground">{QUALITY_LABELS[dimKey] || dimKey}</p>
              <CitationList citations={unique} testId={`quality-citations-${dimKey}`} />
            </div>
          );
        })}
      </div>
    </details>
  );
};

const CoverageLine = ({ coverage }) => {
  if (!coverage) return null;
  const items = [
    ["Eligible consultations", coverage.eligible_consultations],
    ["With written notes", coverage.with_written_note],
    ["Substantive notes", coverage.substantive_notes],
    ["With recorded outcome", coverage.with_outcome],
    ["Follow-up tasks (completed)", `${coverage.followup_tasks ?? 0} (${coverage.followup_tasks_completed ?? 0})`],
    ["Next consultation booked", coverage.with_next_consultation],
    ["Calls logged", coverage.calls_logged],
    ["Documents requested / received", `${coverage.documents_requested ?? 0} / ${coverage.documents_received ?? 0}`],
    ["Lost reasons recorded", coverage.lost_with_reason],
    ["Reschedules / no-shows", `${coverage.reschedule_events ?? 0} / ${coverage.no_show_events ?? 0}`],
  ];
  return (
    <div data-testid="quality-evidence-coverage">
      <p className="text-[10px] text-muted-foreground uppercase tracking-wide mb-0.5">Evidence coverage</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-0.5 text-[11px]">
        {items.map(([k, v]) => (
          <div key={k} className="flex justify-between gap-2"><span className="text-muted-foreground">{k}</span><span className="tabular-nums">{v ?? 0}</span></div>
        ))}
        <div className="flex justify-between gap-2"><span className="text-muted-foreground">Consultation transcripts</span><span>Not available in SalesOS</span></div>
      </div>
    </div>
  );
};

const QualityPanel = ({ quality }) => {
  const isLegacy = (quality?.methodology_version ?? 1) < CURRENT_QUALITY_METHODOLOGY_VERSION;
  const tier = quality?.evidence_tier;
  const title = quality?.score_label || "Quality Score";
  return (
    <div className="space-y-2" data-testid="quality-panel">
      <div>
        <p className="text-[10px] text-muted-foreground uppercase tracking-wide" data-testid="quality-score-label">{title}</p>
        <p className={`text-xl font-semibold tabular-nums ${qualityColor(quality)}`} data-testid="quality-score-value">
          {quality?.value != null ? <>{fmtScore(quality)}<span className="text-xs text-muted-foreground font-normal">/100</span></>
            : <span className="text-sm">No score</span>}
        </p>
        {scoreInterpretation(quality?.value) && (
          <p className="text-[10px] text-muted-foreground" data-testid="quality-score-interpretation">
            {fmtScore(quality)}/100 — {scoreInterpretation(quality?.value)}
          </p>
        )}
        <div className="flex items-center gap-1 flex-wrap">
          {quality?.confidence && <Badge variant="outline" className="text-[9px] capitalize">{quality.confidence.replaceAll("_", " ")} confidence</Badge>}
          {quality?.provisional && <Badge variant="outline" className="text-[9px] border-amber-500/50 text-amber-700 dark:text-amber-400">Provisional</Badge>}
          <Badge variant="outline" className="text-[9px]" data-testid="quality-methodology-version">
            Methodology v{quality?.methodology_version ?? 1}{isLegacy ? " (legacy)" : ""}
          </Badge>
        </div>
      </div>
      {tier && (
        <p className="text-[11px]" data-testid="quality-evidence-tier">
          <span className="text-muted-foreground">Evidence level: </span>{tier.label}
          {tier.max_score != null && <span className="text-muted-foreground"> (max {tier.max_score})</span>}
        </p>
      )}
      {(quality?.cap_reason || quality?.attribution_note) && (
        <p className={`flex gap-1.5 rounded border px-2 py-1.5 text-[11px] ${quality?.provisional || quality?.value == null
          ? "border-amber-500/40 bg-amber-500/5 text-amber-800 dark:text-amber-300" : "text-muted-foreground"}`}
          data-testid="quality-cap-reason">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
          <span className="break-words">{quality.attribution_note || quality.cap_reason}</span>
        </p>
      )}
      {tier ? (
        <div className="space-y-0.5" data-testid="quality-dimension-breakdown">
          {QUALITY_KEYS.map((k) => {
            const c = quality?.components?.[k];
            const m = c?.detail?.measurement_state || "not_enough_evidence";
            const meta = MEASUREMENT[m] || MEASUREMENT.not_enough_evidence;
            return (
              <div key={k} className="flex items-center justify-between gap-2 text-xs" data-testid={`quality-dim-${k}`}>
                <span className="text-muted-foreground truncate">{QUALITY_LABELS[k]}</span>
                <span className="flex items-center gap-1.5 shrink-0">
                  <Badge variant="outline" className={`text-[9px] font-normal ${meta.tone}`}>{meta.label}</Badge>
                  <span className={`font-mono tabular-nums w-14 text-right ${m === "not_enough_evidence" ? "text-muted-foreground italic font-sans text-[10px]" : ""}`}>
                    {m === "not_enough_evidence" ? "unmeasured" : m === "not_applicable" ? "n/a"
                      : c?.score != null ? `${c.score.toFixed(1)}/5` : "—"}
                  </span>
                </span>
              </div>
            );
          })}
          <p className="text-[10px] text-muted-foreground pt-0.5">
            "Not enough evidence" means nothing on record could show this either way — it scores zero
            (no invented credit) but is not a judgement that it was done badly.
          </p>
        </div>
      ) : (
        <ComponentBreakdown components={quality?.components} keys={QUALITY_KEYS} />
      )}
      {quality?.ethical_selling_advisory && (
        <div className="text-[10px] text-muted-foreground">
          Ethical selling (advisory only, not counted in Quality):{" "}
          <Badge variant="outline" className="text-[9px] capitalize">
            {quality.ethical_selling_advisory.state?.replaceAll("_", " ")}
          </Badge>
        </div>
      )}
      <CoverageLine coverage={quality?.evidence_coverage} />
    </div>
  );
};

const ConsultantDetail = ({ userId, periodQuery, name, onGenerated }) => {
  const { hasCap } = useAuth();
  const [snapshot, setSnapshot] = useState(undefined);
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get(`/ai/consultants/${userId}/history?${periodQuery}&limit=1`)
      .then((r) => setSnapshot(r.data[0] || null)).catch(() => setSnapshot(null));
  };
  useEffect(() => { setSnapshot(undefined); load(); }, [userId, periodQuery]); // eslint-disable-line react-hooks/exhaustive-deps

  const generate = async () => {
    setBusy(true);
    try {
      await api.post(`/ai/consultants/${userId}/generate?${periodQuery}`);
      toast.success("Consultant performance generated");
      load();
      onGenerated?.();
    } catch (e) { toast.error(errDetail(e, "Could not generate")); } finally { setBusy(false); }
  };

  if (snapshot === undefined) return null;
  const effort = snapshot?.effort_score;
  const quality = snapshot?.quality_score;
  const commercial = snapshot?.commercial_results;
  const momentum = snapshot?.customer_momentum;
  const eva = snapshot?.expected_vs_actual;

  return (
    <Card data-testid={`consultant-detail-${userId}`}>
      <CardContent className="pt-4 space-y-3">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <p className="font-heading text-sm font-semibold">{name}</p>
          {hasCap("ai.consultant.generate") && (
            <Button size="sm" variant="outline" className="h-7 text-xs" disabled={busy} onClick={generate} data-testid="consultant-generate-button">
              {busy ? "Generating…" : snapshot ? "Regenerate" : "Generate"}
            </Button>
          )}
        </div>
        {!snapshot ? (
          <p className="text-xs text-muted-foreground">No performance snapshot yet for this exact period.</p>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Effort Score</p>
                <p className={`text-xl font-semibold tabular-nums ${scoreColor(effort?.value)}`}>{fmtScore(effort)}<span className="text-xs text-muted-foreground font-normal">/100</span></p>
                {scoreInterpretation(effort?.value) && (
                  <p className="text-[10px] text-muted-foreground" data-testid="effort-score-interpretation">
                    {fmtScore(effort)}/100 — {scoreInterpretation(effort?.value)}
                  </p>
                )}
                <div className="flex gap-1 flex-wrap">
                  {effort?.confidence && <Badge variant="outline" className="text-[9px] capitalize">{effort.confidence} confidence</Badge>}
                  {effort && (effort.methodology_version ?? 1) < CURRENT_EFFORT_METHODOLOGY_VERSION && (
                    <Badge variant="outline" className="text-[9px]">Legacy methodology</Badge>
                  )}
                </div>
                <div className="mt-2"><ComponentBreakdown components={effort?.components} keys={EFFORT_KEYS} /></div>
              </div>
              <QualityPanel quality={quality} />
            </div>
            <QualityEvidenceSection userId={userId} periodQuery={periodQuery}
              hasEvidenceIds={Object.values(quality?.components || {}).some(
                (c) => (c?.detail?.evidence_ids || []).length > 0)} />
            {commercial && (
              <div className="rounded border px-2.5 py-2" data-testid="commercial-results-block">
                <p className="text-[10px] text-muted-foreground uppercase tracking-wide mb-1">
                  Commercial Results <span className="normal-case">(tracked separately — never part of the Quality score)</span>
                </p>
                <p className="text-xs" data-testid="commercial-conversion-count">
                  Commercially converted this period (credited): <span className="font-semibold">{commercial.commercial_conversion_count ?? 0}</span>
                  {(commercial.commercial_conversion_count ?? 0) === 0 && (
                    <span className="text-muted-foreground"> — no closures were credited in this range. This is shown as-is and does not lower or raise Quality.</span>
                  )}
                </p>
                <div className="mt-1"><ComponentBreakdown components={commercial} keys={COMMERCIAL_RESULT_KEYS} /></div>
              </div>
            )}
            {momentum && (
              <div className="rounded border border-dashed px-2.5 py-2" data-testid="customer-momentum-block">
                <div className="text-[10px] text-muted-foreground uppercase tracking-wide mb-1 flex items-center gap-1.5 flex-wrap">
                  {momentum.label}
                  <Badge variant="outline" className="text-[9px] normal-case">Advisory only</Badge>
                  <Badge variant="outline" className="text-[9px] normal-case">AI-estimated</Badge>
                </div>
                <div className="flex justify-between gap-2 text-xs max-w-[240px]">
                  <span className="text-muted-foreground">Closing probability improvement</span>
                  <span className="font-mono tabular-nums">
                    {momentum.closing_probability_improvement?.score != null
                      ? momentum.closing_probability_improvement.score.toFixed(1) : "—"}
                  </span>
                </div>
                <p className="text-[10px] text-muted-foreground mt-1">{momentum.disclaimer}</p>
              </div>
            )}
            {eva?.sample_size != null && (
              <p className="text-xs text-muted-foreground">
                Expected vs actual closures: {eva.expected_closures ?? "—"} expected · {eva.actual_closures ?? "—"} actual
                {" "}(sample size {eva.sample_size})
              </p>
            )}
            <p className="text-[10px] text-muted-foreground">Generated {fmtIST(snapshot.generated_at)} · Advisory only — AI can be wrong, verify against records</p>
          </>
        )}
      </CardContent>
      <ReviewHistory userId={userId} periodQuery={periodQuery} />
      <CoachingSection userId={userId} periodQuery={periodQuery} />
    </Card>
  );
};

// Legacy narrative pass (consultant_ai_reviews) — read-only, labelled.
const ReviewHistory = ({ userId, periodQuery }) => {
  const [reviews, setReviews] = useState([]);
  useEffect(() => {
    api.get(`/ai/consultants/${userId}/reviews?${periodQuery}`)
      .then((r) => setReviews(r.data)).catch(() => setReviews([]));
  }, [userId, periodQuery]);
  const shown = reviews.filter((r) => r.areas_for_attention?.length > 0);
  if (shown.length === 0) return null;
  return (
    <CardContent className="pt-0 border-t mt-1">
      <details className="mt-3">
        <summary className="cursor-pointer text-[10px] text-muted-foreground uppercase tracking-wide">
          Legacy AI review history ({shown.length})
        </summary>
        <div className="space-y-2 mt-2" data-testid="consultant-review-history">
          {shown.map((r) => (
            <div key={r.review_id} className="text-xs border-l-2 pl-2">
              <span className="text-muted-foreground">{fmtIST(r.generated_at)}</span> — {r.areas_for_attention[0]}
            </div>
          ))}
        </div>
      </details>
    </CardContent>
  );
};

const UnresolvedAttribution = ({ data }) => {
  const [open, setOpen] = useState(false);
  if (!data || !data.total) return null;
  return (
    <Card data-testid="consultant-unresolved-attribution">
      <CardContent className="pt-3 pb-3 space-y-1.5 text-xs">
        <button type="button" onClick={() => setOpen(!open)} className="flex items-center gap-1 font-medium text-left">
          {open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
          {data.total} attended consultation{data.total === 1 ? "" : "s"} with unresolved performer attribution
        </button>
        <p className="text-muted-foreground">
          The person who actually ran these could not be confirmed, so they are credited to no one (never
          guessed from assignment or ownership). Confirm the performer in Administration → Data Integrity.
        </p>
        {open && (
          <ul className="space-y-1">
            {data.rows.map((r) => (
              <li key={r.appointment_id} className="border-l-2 pl-2 break-words">
                {r.customer_name || "Customer"} · {r.product_code || "—"} · {fmtIST(r.scheduled_start)}
                <span className="text-muted-foreground"> — assigned to {r.assigned_name || "nobody"}. {r.reason}</span>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
};

const ROSTER_PAGE_SIZE = 20;

const STATUS_LABELS = { conducted: "Conducted", no_show: "No-show", cancelled: "Cancelled", upcoming: "Upcoming", needs_outcome: "Started, no outcome recorded", other_status: "Other" };

// R17 - the REAL work in the period, straight from the appointment records: no AI score, no snapshot needed. Assigned is not performed; a consultation whose performer is not confirmed is credited to no one.
const WorkInPeriod = ({ activity }) => {
  if (!activity) return null;
  const st = activity.status || {};
  const rows = activity.consultants || [];
  return (
    <Card data-testid="consultant-work-in-period">
      <CardContent className="pt-3 pb-3 space-y-2 text-xs">
        <p className="font-medium">Work in this period — from the consultation records (no AI scoring involved)</p>
        <p className="text-muted-foreground" data-testid="consultant-work-summary">
          {activity.cohort_appointments} scheduled{Object.keys(STATUS_LABELS).filter((k) => st[k] > 0).map((k) => ` · ${st[k]} ${STATUS_LABELS[k].toLowerCase()}`).join("")}
          {activity.conducted_by ? ` · of the conducted: ${activity.conducted_by.confirmed_performer} with a confirmed performer, ${activity.conducted_by.unresolved_performer} not confirmed` : ""}
          {activity.truncated ? " · INCOMPLETE (source truncated)" : ""}
        </p>
        {rows.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="consultant-work-table">
              <thead><tr className="text-left text-muted-foreground"><th className="pr-2">Consultant</th><th className="pr-2 text-right">Assigned</th><th className="pr-2 text-right">Conducted (assigned)</th><th className="pr-2 text-right">Performed (confirmed)</th><th className="text-right">Performed with a recorded Signup / Agreed</th></tr></thead>
              <tbody>
                {rows.map((c) => (
                  <tr key={c.consultant_id || "unassigned"} className="border-t">
                    <td className="pr-2">{c.consultant}</td><td className="pr-2 text-right tabular-nums">{c.assigned_scheduled}</td><td className="pr-2 text-right tabular-nums">{c.assigned_conducted}</td>
                    <td className="pr-2 text-right tabular-nums">{c.performed_confirmed}</td><td className="text-right tabular-nums">{c.performed_recorded_signup_or_agreed}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export const ConsultantPerformanceReport = ({ reportsRange = null }) => {
  const { state, setState, periodType, from, to, rangeError, periodQuery } = useConsultantPerformanceUrlState();
  const productCode = state.cp_product;
  const page = state.cp_page;
  const showNoData = state.cp_nodata;
  const selected = state.cp_user || null;
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [rows, setRows] = useState(null);
  const [period, setPeriod] = useState(null);
  const [unresolved, setUnresolved] = useState(null);
  const [noDataRows, setNoDataRows] = useState(null);
  const [rosterVersion, setRosterVersion] = useState(0);
  // R17: what the roster is for (explicit scope), the real work in the period (no AI needed), WHY it is empty, and a load failure that is never shown as "nobody"
  const [scopeBlock, setScopeBlock] = useState(null);
  const [activity, setActivity] = useState(null);
  const [emptyState, setEmptyState] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const productScope = state.cp_scope === "exact" ? "exact" : "family";
  const [productsReady, setProductsReady] = useState(false);
  const rootChosen = isFamilyRoot(products, productCode);
  const scopeReady = productCode === "all" || productsReady;

  useEffect(() => { api.get("/products").then((r) => setProducts(r.data || [])).catch(() => setProducts([])).finally(() => setProductsReady(true)); }, []);

  useEffect(() => {
    if (showNoData || !periodQuery) { setRows(periodQuery ? rows : []); return; }
    if (!scopeReady) return;          // the scope (family vs exact) depends on the product list - never ask before it is known
    if (rosterVersion === 0) setRows(null);
    const params = new URLSearchParams(periodQuery);
    params.set("page", page); params.set("page_size", ROSTER_PAGE_SIZE);
    if (productCode !== "all") { params.set("product_code", productCode); params.set("product_scope", rootChosen ? productScope : "exact"); }
    setLoadError(null);
    api.get(`/ai/consultants?${params}`)
      .then((r) => {
        setRows(r.data.consultants); setTotal(r.data.total ?? r.data.consultants.length);
        setPeriod(r.data.period || null); setUnresolved(r.data.unresolved_attribution || null);
        setScopeBlock(r.data.scope || null); setActivity(r.data.activity || null); setEmptyState(r.data.empty_state || null);
      })
      .catch((e) => { setRows(null); setTotal(0); setLoadError(errDetail(e, "Could not load Consultant Performance")); });
  }, [periodQuery, productCode, productScope, rootChosen, scopeReady, page, showNoData, rosterVersion]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!showNoData || !periodQuery) return;
    if (!scopeReady) return;
    setNoDataRows(null);
    const params = new URLSearchParams(periodQuery);
    params.set("include_no_data", "true");
    if (productCode !== "all") { params.set("product_code", productCode); params.set("product_scope", rootChosen ? productScope : "exact"); }
    api.get(`/ai/consultants?${params}`)
      .then((r) => { setNoDataRows(r.data.employees || []); setPeriod(r.data.period || null); })
      .catch((e) => { setNoDataRows(null); setLoadError(errDetail(e, "Could not load the employees with no consultation data")); });
  }, [periodQuery, productCode, productScope, rootChosen, scopeReady, showNoData]);

  // R17: this tab has its OWN period selector. One click copies the Reports page's range and product scope here (the Reports range is rolling "last N days" or a custom range; here both end days are included, IST).
  const matchReports = () => {
    if (!reportsRange) return;
    const today = istToday();
    let from = reportsRange.date_from; let to = reportsRange.date_to;
    if (!from || !to) {
      const n = Math.max(1, parseInt(reportsRange.days || "30", 10));
      to = today; from = new Date(Date.parse(today) - (n - 1) * 86400000).toISOString().slice(0, 10);
    }
    if (to > today) to = today;
    setState({ cp_period: "custom", cp_from: from, cp_to: to, cp_product: reportsRange.product || "all", cp_scope: reportsRange.scope === "exact" ? "exact" : "family", cp_page: 1, cp_user: "" });
  };

  const totalPages = Math.max(1, Math.ceil(total / ROSTER_PAGE_SIZE));
  const setPeriodType = (v) => {
    const today = istToday();
    // Switching to Custom pre-fills a sensible range (the last 7 days) so
    // the view never lands empty; the user edits from there.
    const extra = v === "custom" && (!from || !to)
      ? { cp_from: new Date(Date.parse(today) - 6 * 86400000).toISOString().slice(0, 10), cp_to: today } : {};
    setState({ cp_period: v, cp_page: 1, cp_user: "", ...extra });
  };

  return (
    <div className="space-y-3" data-testid="consultant-performance-report">
      <div className="flex items-center gap-2 flex-wrap">
        <p className="text-sm font-heading font-semibold flex items-center gap-1.5 mr-auto"><Users className="h-4 w-4" />Team overview</p>
        <Select value={productCode} onValueChange={(v) => setState({ cp_product: v, cp_page: 1, cp_user: "" })}>
          <SelectTrigger data-testid="consultant-product-filter" className="h-8 w-40 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All products</SelectItem>
            {products.map((p) => <SelectItem key={p.code} value={p.code}>{p.name || p.code}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={periodType} onValueChange={setPeriodType}>
          <SelectTrigger data-testid="consultant-period-filter" className="h-8 w-44 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>{PERIOD_OPTIONS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
        </Select>
        {periodType === "custom" && (
          <div className="flex items-center gap-1.5 flex-wrap" data-testid="consultant-custom-range">
            <Input type="date" className="h-8 w-[9.5rem] text-xs" value={from} max={istToday()}
              aria-label="Start date" data-testid="consultant-range-start"
              onChange={(e) => setState({ cp_from: e.target.value, cp_page: 1, cp_user: "" })} />
            <span className="text-xs text-muted-foreground">to</span>
            <Input type="date" className="h-8 w-[9.5rem] text-xs" value={to} max={istToday()}
              aria-label="End date" data-testid="consultant-range-end"
              onChange={(e) => setState({ cp_to: e.target.value, cp_page: 1, cp_user: "" })} />
          </div>
        )}
        {rootChosen && (
          <div className="flex items-center rounded border overflow-hidden h-8" role="group" aria-label="Product scope" data-testid="consultant-scope-toggle">
            <button type="button" data-testid="consultant-scope-family" aria-pressed={productScope === "family"} onClick={() => setState({ cp_scope: "family", cp_page: 1, cp_user: "" })}
              className={`px-2 text-xs ${productScope === "family" ? "bg-primary text-primary-foreground" : "bg-background"}`}>Whole family</button>
            <button type="button" data-testid="consultant-scope-exact" aria-pressed={productScope === "exact"} onClick={() => setState({ cp_scope: "exact", cp_page: 1, cp_user: "" })}
              className={`px-2 text-xs border-l ${productScope === "exact" ? "bg-primary text-primary-foreground" : "bg-background"}`}>This code only</button>
          </div>
        )}
        {reportsRange && <Button size="sm" variant="outline" className="h-8 text-xs" data-testid="consultant-match-reports" onClick={matchReports}>Match the Reports range</Button>}
        <Button size="sm" variant={showNoData ? "default" : "outline"} className="h-8 text-xs"
          data-testid="consultant-show-no-data-toggle"
          onClick={() => setState({ cp_nodata: !showNoData, cp_user: "" })}>
          {showNoData ? "Showing: no eligible consultations" : "Show employees with no consultation data"}
        </Button>
      </div>
      {rangeError ? (
        <p className="text-xs text-red-600 dark:text-red-400" data-testid="consultant-range-error">{rangeError}</p>
      ) : period?.label ? (
        <>
          <p className="text-[11px] text-muted-foreground" data-testid="consultant-period-label">
            Period: {period.label} — the same range applies to the roster, Effort, Quality, coaching, evidence, commercial results and history.
          </p>
          {scopeBlock && <ReportScopeBanner scope={scopeBlock} testId="consultant-scope-banner" />}
          {scopeBlock && <p className="text-[11px] text-muted-foreground" data-testid="consultant-own-period-note">{scopeBlock.note}</p>}
        </>
      ) : null}
      {loadError && (
        <div role="alert" className="rounded border border-[hsl(var(--danger))]/40 bg-[hsl(var(--danger))]/10 p-3 text-sm space-y-2" data-testid="consultant-load-error">
          <p><strong>Consultant Performance could not be loaded.</strong> {loadError} Nothing is shown rather than an empty list that looks like "nobody worked".</p>
          <Button size="sm" variant="outline" data-testid="consultant-retry" onClick={() => setRosterVersion((v) => v + 1)}>Try again</Button>
        </div>
      )}

      {rangeError ? null : showNoData ? (
        noDataRows === null ? (
          loadError ? null : <p className="text-xs text-muted-foreground">Loading…</p>
        ) : noDataRows.length === 0 ? (
          <EmptyState icon={Sparkles} title="Every active employee has at least one eligible consultation this period" />
        ) : (
          <div className="grid gap-2" data-testid="consultant-no-data-list">
            {noDataRows.map((r) => (
              <Card key={r.user_id}>
                <CardContent className="pt-3 pb-3 flex items-center gap-2 sm:gap-4 flex-wrap">
                  <p className="font-medium text-sm flex-1 min-w-32">{r.name}</p>
                  <span className="text-xs text-muted-foreground capitalize">{r.role}</span>
                  <Badge variant="outline" className="text-[9px]" data-testid="no-data-status">{r.status_label || "No eligible consultations"}</Badge>
                  <span className="text-[10px] text-muted-foreground">No Quality score · no coaching</span>
                  {r.assigned_unconfirmed_count > 0 && (
                    <span className="text-[10px] text-amber-700 dark:text-amber-400 basis-full">
                      {r.assigned_unconfirmed_count} assigned consultation(s) have unresolved performer attribution.
                    </span>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )
      ) : loadError ? null : rows === null ? (
        <p className="text-xs text-muted-foreground">Loading…</p>
      ) : (
        <>
          <UnresolvedAttribution data={unresolved} />
          <WorkInPeriod activity={activity} />
          {rows.length === 0 ? (
            <div data-testid="consultant-empty-state" data-reason={emptyState?.reason || "unknown"}>
              <EmptyState icon={Sparkles} title={emptyState ? emptyState.message : "No employees with eligible consultations this period"}
                hint="Population is activity-based — every employee confirmed to have conducted at least one attended consultation in this period appears here, regardless of role." />
            </div>
          ) : (
            <div className="grid gap-2">
              {rows.map((r) => (
                <Card key={r.user_id} data-testid={`consultant-row-${r.user_id}`}
                  className={`cursor-pointer transition-colors ${selected === r.user_id ? "border-primary" : "hover:border-primary/50"}`}
                  onClick={() => setState({ cp_user: selected === r.user_id ? "" : r.user_id })}>
                  <CardContent className="pt-3 pb-3 flex items-center gap-x-4 gap-y-1 flex-wrap">
                    <div className="flex-1 min-w-40">
                      <p className="font-medium text-sm">{r.name}</p>
                      <p className="text-[10px] text-muted-foreground">
                        <span className="capitalize">{r.role}</span> · {r.eligible_consultation_count} eligible consultation{r.eligible_consultation_count === 1 ? "" : "s"}
                        {r.assigned_unconfirmed_count > 0 && ` · ${r.assigned_unconfirmed_count} more assigned, performer unconfirmed`}
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="text-xs text-muted-foreground">Effort</div>
                      <p className={`font-mono text-sm font-semibold tabular-nums w-8 ${scoreColor(r.effort_score?.value)}`}>{fmtScore(r.effort_score)}</p>
                      <div className="text-xs text-muted-foreground">Quality</div>
                      <p className={`font-mono text-sm font-semibold tabular-nums w-8 ${qualityColor(r.quality_score)}`}>{fmtScore(r.quality_score)}</p>
                      {r.quality_score?.provisional && <Badge variant="outline" className="text-[9px] border-amber-500/50 text-amber-700 dark:text-amber-400" data-testid="roster-provisional">Provisional</Badge>}
                      {!r.has_current_snapshot && <Badge variant="outline" className="text-[9px]">Not yet generated</Badge>}
                    </div>
                  </CardContent>
                </Card>
              ))}
              {totalPages > 1 && (
                <div className="flex items-center gap-2 justify-end">
                  <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setState({ cp_page: page - 1 })}>Prev</Button>
                  <span className="text-xs text-muted-foreground" data-testid="consultant-roster-page-info">
                    Page {page} of {totalPages} ({total} total)
                  </span>
                  <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => setState({ cp_page: page + 1 })}>Next</Button>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {selected && periodQuery && !showNoData && rows && (
        <ConsultantDetail userId={selected} periodQuery={periodQuery}
          name={rows.find((r) => r.user_id === selected)?.name || "Employee"}
          onGenerated={() => setRosterVersion((v) => v + 1)} />
      )}
    </div>
  );
};
